import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilterControls, { type FilterState } from "./components/FilterControls";
import MediaRow from "@/components/feature/MediaRow";
import Pagination from "@/components/base/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  filterMedia,
  getMediaByType,
  sortMedia,
  typeLabel,
} from "@/utils/media";
import type { MediaType } from "@/types/media";

const PAGE_SIZE = 6;

const years = Array.from({ length: 30 }, (_, i) => String(2026 - i));

const defaultFilters: FilterState = {
  region: "全部",
  year: "全部",
  genre: "全部",
  sort: "latest",
};

const typeDesc: Record<MediaType, string> = {
  movie: "汇集院线新片、经典佳作与各类型电影，支持按地区、年份与类型筛选，快速找到你想看的那一部。",
  tv: "收录热播剧集与口碑系列剧，涵盖大陆、港台、欧美、日韩等地区，每日更新，追剧不迷路。",
};

export default function Category() {
  const { type } = useParams();
  const validType: MediaType | null =
    type === "movie" || type === "tv" ? type : null;

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const label = validType ? typeLabel[validType] : "影视";

  usePageMeta(
    `${label}分类 - 茶杯狐影视`,
    validType
      ? typeDesc[validType]
      : "茶杯狐影视分类页，按地区、年份、类型筛选影视资源。",
  );

  const source = useMemo(
    () => (validType ? getMediaByType(validType) : []),
    [validType],
  );

  const filtered = useMemo(
    () =>
      sortMedia(
        filterMedia(source, {
          region: filters.region,
          year: filters.year,
          genre: filters.genre,
        }),
        filters.sort,
      ),
    [source, filters],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const updateFilters = (next: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  if (!validType) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-error-warning-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          未找到该分类
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          请选择“电影”或“电视剧”分类进行浏览。
        </p>
        <div className="mt-6 flex gap-3">
          <Link
            to="/category/movie"
            className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
          >
            浏览电影
          </Link>
          <Link
            to="/category/tv"
            className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400"
          >
            浏览电视剧
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* 分类标题区 */}
      <section className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs text-primary-200">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-50">{label}</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            {label}分类
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
            {typeDesc[validType]}
          </p>
          <p className="mt-4 text-sm text-primary-300">
            共收录 <span className="font-bold text-accent-400">{source.length}</span> 部作品
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        {/* 桌面端筛选栏 */}
        <div className="hidden rounded-lg border border-background-200 bg-background-100/70 p-5 lg:block">
          <FilterControls
            value={filters}
            years={years}
            onChange={updateFilters}
            onReset={resetFilters}
          />
        </div>

        {/* 移动端筛选按钮 */}
        <div className="lg:hidden">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-background-200 bg-background-100/70 px-4 py-3 text-sm font-medium text-foreground-800"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-600" aria-hidden="true" />
              筛选与排序
            </span>
            <i
              className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
              aria-hidden="true"
            />
          </button>
          {showFilters ? (
            <div className="mt-3 rounded-lg border border-background-200 bg-background-100/70 p-4">
              <FilterControls
                value={filters}
                years={years}
                onChange={updateFilters}
                onReset={resetFilters}
              />
            </div>
          ) : null}
        </div>

        {/* 列表 */}
        <div className="mt-6 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            作品列表
            <span className="ml-2 text-sm font-normal text-foreground-400">
              {filtered.length} 条结果
            </span>
          </h2>
        </div>

        {paged.length > 0 ? (
          <div className="mt-4 flex flex-col gap-3">
            {paged.map((item, index) => (
              <MediaRow key={item.id} item={item} index={index} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">
              没有找到匹配的作品
            </h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              试试调整地区、年份或类型条件，或点击下方按钮重置全部筛选。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              重置筛选条件
            </button>
          </div>
        )}

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onChange={setPage}
        />
      </div>
    </div>
  );
}